import { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { ContractLined } from '@tag/tag-icons';
import { useParams } from 'react-router';
import dayjs from 'dayjs';

import cn from 'classnames';

import { Textarea } from '@/components/common/TextAreaTag4/TextareaTag4';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import Page from '@/components/Page/Page';
import PageHeader from '@/components/common/PageHeader/PageHeader';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import useGetVarBillingUsageHeader from './hooks/useGetVarBillingUsageHeader';
import { UsageDetails } from './UsageDetails/UsageDetails';
import Skeleton from '@/components/common/Skeleton/Skeleton';
import { Fields, validationSchema } from './validation';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';

import { FormattingUtils } from '@/utils/Formatting.utils';

import Dropdown from '@/components/common/Dropdown/Dropdown';
import useUpdateUsageHeader from './hooks/useUpdateUsageHeader';

import { FULL_DATE_PATTERN, USAGE_HEADER_FILE_NAME_MAX_CHARS } from '@/config/common.config';
import styles from './file-details.module.scss';
import {
  hideSalerOrderBtnTooltip, disabledHeaderInputStatuses, getdropDownCommonStyling,
  mapEnumToFormattedStatus, disabledMatchActionStatuses
} from
  './helpers/getUsageDetailsHelpers';
import { USAGE_HEADER_STATUS_COLORS } from './helpers/statusColors';
import { Pill } from '@/components/common/Pill/Pill';
import { VarBillingUsageHeaderStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import useMatchUsageDetails from './hooks/useMatchUsageDetails';
import useProcessUsageDetails from './hooks/useProcessUsageDetails';
import { HeaderAction } from '../types';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { useGetProductNames } from './hooks/useGetProductNames';
import { END_DATE_BEFORE_START_DATE, INVOICE_DATE_BEFORE_START_DATE } from '@/config/text.const';

export default function FileDetails() {
  const { fileId }: { fileId: string } = useParams();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isRestrictedRole = isUserRole || isReaderRole;
  const uploadedBy = new URL(window.location.href).searchParams.get('uploadedBy') ?? '';
  const { getVarBillingUsageHeader, varBillingUsageHeader, isLoadingUsageHeader } = useGetVarBillingUsageHeader();
  const { productNames, isLoadingProductNames } = useGetProductNames();
  const { updateUsageHeader } = useUpdateUsageHeader();
  const { matchUsageDetails } = useMatchUsageDetails();
  const { processUsageDetails } = useProcessUsageDetails();
  const productNamesOptions = productNames.map(product => ({ name: product }));
  const defaultProduct = varBillingUsageHeader?.productName ? { name: varBillingUsageHeader.productName } : undefined;
  const createdDate = varBillingUsageHeader?.importDocument?.created;
  const documentStatus = mapEnumToFormattedStatus(varBillingUsageHeader?.status);
  const fileNameTrimmed = FormattingUtils.getValueTrimmed(varBillingUsageHeader?.importDocument?.fileName ?? '',
    USAGE_HEADER_FILE_NAME_MAX_CHARS);
  const fileHeaderText = `${uploadedBy ? `by ${uploadedBy}` : ''} on ${dayjs(createdDate).format(FULL_DATE_PATTERN)}`;

  const isGenerateSODisabled = varBillingUsageHeader?.status !== VarBillingUsageHeaderStatusEnum.Matched;

  const isdMatchedDisabled = Boolean(varBillingUsageHeader?.status &&
    disabledMatchActionStatuses.includes(varBillingUsageHeader?.status));

  const isInputDisabled = Boolean(varBillingUsageHeader?.status &&
    disabledHeaderInputStatuses.includes(varBillingUsageHeader?.status));

  useEffect(() => {
    if (fileId) {
      getVarBillingUsageHeader(fileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const { values, setFieldValue, errors, resetForm, isValid, setFieldTouched, touched, dirty } = useFormik<Fields>({
    initialValues: {
      invoiceDate: varBillingUsageHeader?.invoiceDate ?? undefined,
      billingStartDate: varBillingUsageHeader?.billingStartDate ?? undefined,
      billingEndDate: varBillingUsageHeader?.billingEndDate ?? undefined,
      productName: defaultProduct?.name ?? undefined,
      notes: varBillingUsageHeader?.notes ?? undefined,
      generationOrMatchWasTriggered: false
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => undefined,
  });
  // We need this separate logic for Save draft and match to contracts, which is diff from Generate SO validation logic;
  const draftActionIsValid = !errors.notes && errors.invoiceDate !== INVOICE_DATE_BEFORE_START_DATE
    && errors.billingEndDate !== END_DATE_BEFORE_START_DATE;
  const dirtyUsageHeaders = Object.keys(touched).length > 0;
  const requiredFieldIsEmpty = !values.billingEndDate || !values.billingStartDate || !values.invoiceDate;
  const handleSaveDraft = () => {
    if (varBillingUsageHeader?.usageHeaderID) {
      updateUsageHeader({
        invoiceDate: values.invoiceDate,
        billingStartDate: values.billingStartDate,
        billingEndDate: values.billingEndDate,
        notes: values.notes,
        productName: values.productName,
        usageHeaderId: varBillingUsageHeader.usageHeaderID
      }, resetForm);
    }
  };

  const handleMatchOrProcess = (action: HeaderAction) => {
    if (varBillingUsageHeader?.usageHeaderID) {
      const usageHeader = {
        invoiceDate: values.invoiceDate,
        billingStartDate: values.billingStartDate,
        billingEndDate: values.billingEndDate,
        notes: values.notes,
        productName: values.productName,
        usageHeaderId: varBillingUsageHeader.usageHeaderID
      };
      return action === HeaderAction.PROCESS ?
        processUsageDetails(usageHeader, resetForm) :
        matchUsageDetails(usageHeader, resetForm);
    }
  };

  const handleAction = async (action: HeaderAction) => {
    const fieldErrors = await setFieldValue('generationOrMatchWasTriggered', true, true);
    if (fieldErrors && Object.keys(fieldErrors).length === 0) {
      handleMatchOrProcess(action);
    }
  };

  return (
    <>
      <Page pageTitle="File Details" padding={true} withWhiteBackground={true}>
        <div className="pt-20">
          <div className={styles['page-header']}>
            <PageHeader
              isLoading={isLoadingUsageHeader}
              title={`File ${fileNameTrimmed} - imported ${fileHeaderText}`}
              icon={<ContractLined />}
            />
            {isLoadingUsageHeader ? (
              <Skeleton width={100} count={1} className={styles['status-skeleton']} />
            ) : documentStatus && (
              <span className={styles['status-title']}>
                <Pill
                  name={documentStatus}
                  title={documentStatus}
                  size="large"
                  className={styles['status-pill']}
                  background={USAGE_HEADER_STATUS_COLORS[varBillingUsageHeader?.status ?? 'Draft']}
                >
                  {documentStatus}
                </Pill>
              </span>
            )}
          </div>
          <>
            <form>
              {!isRestrictedRole && <div className={styles['usage-header-container']}>
                <div className={styles['header-group']}>
                  <TopLabel required label='Invoice Date' id="invoice-date-input" />
                  {isLoadingUsageHeader ?
                    <Skeleton /> :
                    <>
                      <DatePickerTag4
                        value={values?.invoiceDate ? new Date(values.invoiceDate) : undefined}
                        id="invoice-date-input"
                        disabled={isInputDisabled}
                        onChange={(e) => {
                          setFieldTouched('invoiceDate', true, false);
                          setFieldValue('invoiceDate', e.value ?? null);
                        }}
                      />
                      {errors.invoiceDate && (
                        <ValidationAlert isFloating={false}>{errors.invoiceDate}</ValidationAlert>
                      )}
                    </>

                  }
                </div>
                <div className={styles['header-group']}>
                  <TopLabel required label='Billing Start Date' id="start-date-input" />
                  {isLoadingUsageHeader ?
                    <Skeleton /> :
                    <>
                      <DatePickerTag4
                        value={values?.billingStartDate ? new Date(values.billingStartDate) : undefined}
                        id="start-date-input"
                        disabled={isInputDisabled}
                        onChange={(e) => {
                          setFieldTouched('billingStartDate', true, false);
                          setFieldValue('billingStartDate', e.value ?? null);
                        }}
                      />
                      {errors.billingStartDate && (
                        <ValidationAlert isFloating={false}>{errors.billingStartDate}</ValidationAlert>
                      )}
                    </>
                  }
                </div>
                <div className={styles['header-group']}>
                  <TopLabel required label='Billing End Date' id="end-date-input" />
                  {isLoadingUsageHeader ?
                    <Skeleton /> :
                    <>
                      <DatePickerTag4
                        value={values?.billingEndDate ? new Date(values.billingEndDate) : undefined}
                        id="end-date-input"
                        disabled={isInputDisabled}
                        onChange={(e) => {
                          setFieldTouched('billingEndDate', true, false);
                          setFieldValue('billingEndDate', e.value ?? null);
                        }}
                      />
                      {errors.billingEndDate && (
                        <ValidationAlert isFloating={false}>{errors.billingEndDate}</ValidationAlert>
                      )}
                    </>
                  }
                </div>
                <div className={styles['header-group']}>
                  <TopLabel label='Product Name' id="product-name-input" />
                  {isLoadingUsageHeader ?
                    <Skeleton /> :
                    <div className={cn({ [styles.disabled]: isInputDisabled })}>
                      <Dropdown
                        disabled={isInputDisabled}
                        id="product-name-input"
                        isClearable={true}
                        optionsData={productNamesOptions}
                        defaultValue={defaultProduct}
                        isLoading={isLoadingProductNames}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.name}
                        menuPortalTarget={document.querySelector('body')}
                        isSearchable={true}
                        placeholder={<div className={styles['dropdown-placeholder']}>Select product name...</div>}
                        customStyles={{
                          container: (provided, { isDisabled }) => ({
                            ...provided,
                            ...getdropDownCommonStyling(isDisabled)
                          }),
                        }}
                        handleChange={(newValue) => {
                          setFieldTouched('productName', true, false);
                          setFieldValue('productName', newValue?.name ?? null);
                        }}
                      />
                    </div>
                  }
                </div>
                <div className={styles['header-group']}>
                  <TopLabel label='Notes' id="notes-input" />
                  {isLoadingUsageHeader ?
                    <Skeleton /> :
                    <>
                      <Textarea
                        className={cn(styles['text-area'], {
                          [styles.disabled]: isInputDisabled
                        })}
                        id="notes-input"
                        disabled={isInputDisabled}
                        value={values?.notes ?? undefined}
                        onChange={(e) => {
                          setFieldTouched('notes', true, false);
                          setFieldValue('notes',
                            FormattingUtils.replaceControlCharacters(e.value)
                              ? e.value
                              : null);
                        }}
                      />
                      {errors.notes && touched.notes && (
                        <ValidationAlert isFloating={false}>{errors.notes}</ValidationAlert>
                      )}
                    </>
                  }
                </div>
              </div>}
              {!isRestrictedRole && <div className={styles['buttons-container']}>
                <Button className={styles['draft-btn']} onClick={handleSaveDraft}
                  disabled={!dirtyUsageHeaders || !draftActionIsValid}
                  buttonStyleType={ButtonStyleType.SECONDARY}>
                  Save draft
                </Button>
                <Button className={styles['match-btn']}
                  onClick={() => handleAction(HeaderAction.MATCH)}
                  disabled={isdMatchedDisabled || !isValid || requiredFieldIsEmpty}
                  title={isValid && !requiredFieldIsEmpty ? undefined :
                    'Matching can be initiated only at the required header fields completed.'}>
                  Match to contracts
                </Button>
                <Button buttonStyleType={ButtonStyleType.PRIMARY}
                  onClick={() => handleAction(HeaderAction.PROCESS)}
                  disabled={isGenerateSODisabled || !isValid}
                  title={hideSalerOrderBtnTooltip(varBillingUsageHeader?.status) ? undefined :
                    'Generation of Sales Orders is enabled for fully matched sets only'}>
                  Generate Sales Orders
                </Button>
              </div>}
            </form>
            <UsageDetails
              usageHeaderID={varBillingUsageHeader?.usageHeaderID}
              usageHeaderStatus={varBillingUsageHeader?.status}
              usageHeaderFileName={varBillingUsageHeader?.importDocument?.fileName}
              getVarBillingUsageHeader={getVarBillingUsageHeader}
              usageHeaderModified={dirty} resetForm={resetForm} />
          </>
        </div>
      </Page >
    </>
  );
}
